import {Component, Input, OnDestroy, OnInit} from "@angular/core";
import {Website} from "../../website";
import {filter, first, switchMap} from "rxjs/operators";
import {ActivatedRoute} from "@angular/router";
import * as fromStore from "../../../../store";
import {combineLatest} from "rxjs";
import {Utils} from "../../../../shared/utils/utils";
import {Store} from "@ngrx/store";
import {EcommerceService} from "../ecommerce.service";
import {TypeUtils} from "../../../../shared/utils/type-utils";
import { DateAdapter } from '@angular/material/core';
import { RangePresetKey } from '../mat-calendar/custom-header/custom-range-panel/custom-range-panel.component';


@Component({
    selector: 'db-ecommerce-overview',
    templateUrl: './ecommerce-overview.component.html',
    styleUrls: ['./ecommerce-overview.component.scss'],
    standalone: false
})
export class EcommerceOverviewComponent implements OnInit, OnDestroy {

  @Input() website: Website;
  public loading: boolean;
  public loadingBtn: boolean;
  public demoUser: boolean;
  public performanceInfoLastYear;
  public performanceInfoNow;
  public netSalesData;
  public ordersData;
  public dateRange;
  public dateRangeLastYear;
  public selectedDates;
  public customPresets = [
    RangePresetKey.TODAY,
    RangePresetKey.YESTERDAY_TODAY,
    RangePresetKey.YESTERDAY,
    RangePresetKey.LAST_7_DAYS,
    RangePresetKey.LAST_30_DAYS,
    RangePresetKey.THIS_MONTH,
    RangePresetKey.PREVIOUS_MONTH,
    RangePresetKey.PREVIOUS_YEAR
  ];

  constructor(
      private _activatedRoute: ActivatedRoute,
      private _ecommerceService: EcommerceService,
      private dateAdapter: DateAdapter<any>,
      private readonly _store: Store<fromStore.DbStoreState>,
  ) {
  }

  ngOnInit() {
    this.website = this._activatedRoute.snapshot.parent.parent.data['website'];
    this._getUserSubscription();
    this._getTotalSales();
    this._getUpdatedAnalyticsData();
    this._setSelectedDates();
    const dateRangeName = localStorage.getItem('dbEcommerceRangeName' + this.website?.id);

    if (dateRangeName) {
      this._setDateRange(dateRangeName);
    }
    else {
      this._setSelectedDates();
    }
  }

  private _setDateRange(dateRangeName): void {
    let lastYearEndDate;
    let lastYearStartDay;
    const periodNumber = localStorage.getItem('dbEcommercePeriodNumber' + this.website?.id);
    const dateRangeInitialDates = this._calculateDateRange(dateRangeName);
    const dateRangeStartEnd = this._calculateDateRange(dateRangeName);
    const startDate = dateRangeStartEnd[0];
    const endDate = dateRangeStartEnd[1];
    const dateDifference = this._getTwoDatesDifference(startDate, endDate);
    if (periodNumber === 'year') {
      lastYearEndDate = new Date(endDate.setFullYear(endDate.getFullYear() - 1));
      lastYearStartDay = new Date(startDate.setFullYear(startDate.getFullYear() - 1));
    }
    else {
      lastYearEndDate = new Date(endDate.getTime() - dateDifference * 24 * 60 * 60 * 1000);
      lastYearStartDay = new Date(startDate.getTime() - dateDifference * 24 * 60 * 60 * 1000);
    }
    this._setSelectedDatesArray(dateRangeInitialDates[0], dateRangeInitialDates[1], lastYearStartDay, lastYearEndDate);
  }

  private _calculateDateRange(rangeName: string): [start: any, end: any] {
    const today = new Date();

    switch (rangeName) {
      case 'Today':
        return [today, today];

      case 'Yesterday & today': {
        const start = this.dateAdapter.addCalendarDays(today, -1);
        return [start, today];
      }
      case 'Yesterday': {
        const start = this.dateAdapter.addCalendarDays(today, -1);
        return [start, start];
      }
      case 'Last 7 days': {
        const start = this.dateAdapter.addCalendarDays(today, -6);
        return [start, today];
      }
      case 'Last 30 days': {
        const start = this.dateAdapter.addCalendarDays(today, -29);
        return [start, today];
      }
      case 'This month': {
        return this.calculateMonth(today);
      }
      case 'Previous month': {
        const thisDayLastMonth = this.dateAdapter.addCalendarMonths(today, -1);
        return this.calculateMonth(thisDayLastMonth);
      }
      case 'Previous year': {
        const thisDayLastMonth = this.dateAdapter.addCalendarYears(today, -1);
        return this.calculateMonth(thisDayLastMonth);
      }
    }
  }

  private calculateMonth(forDay: any): [start: any, end: any] {
    const year = this.dateAdapter.getYear(forDay);
    const month = this.dateAdapter.getMonth(forDay);
    const start = this.dateAdapter.createDate(year, month, 1);
    const end = this.dateAdapter.addCalendarDays(
      start,
      this.dateAdapter.getNumDaysInMonth(forDay) - 1
    );
    return [start, end];
  }


  private _setSelectedDates(): void {
    const today = new Date();

    const sevenDaysBefore = new Date(today);
    sevenDaysBefore.setDate(today.getDate() - 6);

    const fourteenDaysBefore = new Date(today);
    fourteenDaysBefore.setDate(today.getDate() - 7);

    const twentyOneDaysBefore = new Date(today);
    twentyOneDaysBefore.setDate(today.getDate() - 13);

    this._setSelectedDatesArray(sevenDaysBefore, today, twentyOneDaysBefore, fourteenDaysBefore);
  }

  private _setSelectedDatesArray(startDate, endDate, prevStartDate, prevEndDate) {

    const formatedAfterDateNow = Utils.formatDateYearMonthDay(startDate);
    const formatedBeforeDateNow = Utils.formatDateYearMonthDay(endDate);

    const formatedAfterDateLastWeek = Utils.formatDateYearMonthDay(prevStartDate);
    const formatedBeforeDateLastWeek = Utils.formatDateYearMonthDay(prevEndDate);

    this.selectedDates = {
      'startDate': formatedAfterDateNow,
      'endDate': formatedBeforeDateNow,
      'prevStartDate': formatedAfterDateLastWeek,
      'prevEndDate': formatedBeforeDateLastWeek
    };

    return this.selectedDates;
  }

  public customDateRange(dateRange): void {
    const periodNumber = localStorage.getItem('dbEcommercePeriodNumber' + this.website?.id);
    const dateDifference = this._getTwoDatesDifference(dateRange?.start, dateRange?.end);
    this._callToPerformanceForAnotherPeriod(dateRange, periodNumber, dateDifference + 1);
  }

  public clearData(): void {
    this.loadingBtn = true;
    this._ecommerceService.flushCache(this.website?.id)
        .pipe(
            first(response => !!response),
        )
        .subscribe(response => {
          if (response.success) {
            this._updatePerformance();
          }
          this.loadingBtn = false;
        });
  }

  private _updatePerformance(): void {
    const today = new Date();

    const sevenDaysBefore = new Date(today);
    sevenDaysBefore.setDate(today.getDate() - 6);

    const formatedAfterDateNow = Utils.formatDateYearMonthDay(sevenDaysBefore);
    const formatedBeforeDateNow = Utils.formatDateYearMonthDay(today);

    const afterDateNow = formatedAfterDateNow + 'T00:00:00';
    const beforeDateNow = formatedBeforeDateNow + 'T23:59:59';

    const fourteenDaysBefore = new Date(today);
    fourteenDaysBefore.setDate(today.getDate() - 7);

    const twentyOneDaysBefore = new Date(today);
    twentyOneDaysBefore.setDate(today.getDate() - 13);

    const formatedAfterDateLastWeek = Utils.formatDateYearMonthDay(twentyOneDaysBefore);
    const formatedBeforeDateLastWeek = Utils.formatDateYearMonthDay(fourteenDaysBefore);

    const afterDateLastWeek = formatedAfterDateLastWeek + 'T00:00:00';
    const beforeDateLastWeek = formatedBeforeDateLastWeek + 'T23:59:59';

    const dates = 'after_current=' + afterDateNow + '&before_current=' + beforeDateNow + '&after_previous=' + afterDateLastWeek + '&before_previous=' + beforeDateLastWeek;
    this._store.dispatch(new fromStore.LoadEcommerceAnalytics({websiteId: this.website?.id, datePicker: dates}));}


  private _getTwoDatesDifference(date1, date2): number {
    // Calculate the difference in milliseconds
    const differenceInMilliseconds = date2 - date1;

    // Convert the difference to days
    return Math.floor(differenceInMilliseconds / (1000 * 60 * 60 * 24));
  }

  private _callToPerformanceForAnotherPeriod(dateRange, period, dateDifference): void {


    const formatedAfterDate = Utils.formatDateYearMonthDay(dateRange?.start);
    const formatedBeforeDate = Utils.formatDateYearMonthDay(dateRange?.end);

    const afterDate = formatedAfterDate + 'T00:00:00';
    const beforeDate = formatedBeforeDate + 'T23:59:59';

    let lastYearEndDate;
    let lastYearStartDay;
    if (period === 'year') {
      lastYearEndDate = new Date(dateRange?.end.setFullYear(dateRange?.end.getFullYear() - 1));
      lastYearStartDay = new Date(dateRange?.start.setFullYear(dateRange?.start.getFullYear() - 1));
    }
    else {
      lastYearEndDate = new Date(dateRange?.end.getTime() - dateDifference * 24 * 60 * 60 * 1000);
      lastYearStartDay = new Date(dateRange?.start.getTime() - dateDifference * 24 * 60 * 60 * 1000);
    }

    const formatedAfterDateLastYear = Utils.formatDateYearMonthDay(lastYearStartDay);
    const formatedBeforeDateLastYear = Utils.formatDateYearMonthDay(lastYearEndDate);

    this._setSelectedDatesArray(dateRange?.start, dateRange?.end, lastYearStartDay, lastYearEndDate)

    const afterDateLastYear = formatedAfterDateLastYear + 'T00:00:00';
    const beforeDateLastYear = formatedBeforeDateLastYear + 'T23:59:59';

    const dates = 'after_current=' + afterDate + '&before_current=' + beforeDate + '&after_previous=' + afterDateLastYear + '&before_previous=' + beforeDateLastYear;
    this._store.dispatch(new fromStore.LoadEcommerceAnalytics({websiteId: this.website?.id, datePicker: dates}));
}

  private _mapDataToNewValue(responseData, responseDataLastYear, name): any {
    const data = [
      {
        "name": name + " This Year",
        "series": responseData.map(item => {
          const date = new Date(item.date_start).getDate();
          return {
            name: date,
            value: item.subtotals.orders_count
          };
        })
      },
      {
        "name": name + " Last Year",
        "series": responseDataLastYear.map(item => {
          const date = new Date(item.date_start).getDate();
          return {
            name: date,
            value: item.subtotals.orders_count
          };
        })
      }
    ];
    return data;
  }

  private _getUserSubscription(): void {
    this._store.select(fromStore.getUserLoaded)
        .pipe(
            filter((loaded: boolean) => loaded),
            switchMap(() => combineLatest(
                this._store.select(fromStore.getUser),
                this._store.select(fromStore.getUserSubscription)
            ))
        )
        .subscribe(([user, userSubscription]) => {
          this.demoUser = Utils.isDemoUser(userSubscription, user);
        });
  }


  private _getTotalSales(): void {
    combineLatest(
        this._store.select(fromStore.getEcommercePerformanceLoaded(this.website?.id)),
        this._store.select(fromStore.getEcommercePerformanceNowLoaded(this.website?.id)),
        this._store.select(fromStore.getEcommercePerformanceReportOrdersLoaded(this.website?.id)),
        this._store.select(fromStore.getEcommercePerformanceReportOrdersLastYearLoaded(this.website?.id)),
        this._store.select(fromStore.getEcommercePerformanceReportNetSalesLoaded(this.website?.id)),
        this._store.select(fromStore.getEcommercePerformanceReportNetSalesLastYearLoaded(this.website?.id))
    )
        .pipe(
            first(([totalLoaded, totalNowLoaded, reportOrdersLoaded, reportOrdersLastYearLoaded,reportNetSalesLoaded, reportNetSalesLastYearLoaded]) => !!totalLoaded && !!totalNowLoaded && !!reportOrdersLoaded && !!reportOrdersLastYearLoaded && !!reportNetSalesLoaded && !!reportNetSalesLastYearLoaded),
            switchMap(() =>  combineLatest(
                this._store.select(fromStore.getEcommercePerformanceTotal(this.website.id)),
                this._store.select(fromStore.getEcommercePerformanceTotalNow(this.website.id)),
                this._store.select(fromStore.getEcommercePerformanceReportOrders(this.website.id)),
                this._store.select(fromStore.getEcommercePerformanceReportOrdersLastYear(this.website.id)),
                this._store.select(fromStore.getEcommercePerformanceReportNetSales(this.website.id)),
                this._store.select(fromStore.getEcommercePerformanceReportNetSalesLastYear(this.website.id))
            )),
        )
        .subscribe(([performanceLastMonth, performanceNow, reportOrders, reportOrdersLastYear, reportNetSales, reportNetSalesLastYear]): void => {
          this.performanceInfoLastYear = performanceLastMonth;
          this.performanceInfoNow = performanceNow;
          this.ordersData = TypeUtils.isDefined(reportOrders) && reportOrders.length && TypeUtils.isDefined(reportOrdersLastYear) && reportOrdersLastYear.length && this._mapDataToNewValue(reportOrders, reportOrdersLastYear, "Orders");
          this.netSalesData = TypeUtils.isDefined(reportNetSales) && reportNetSales.length && TypeUtils.isDefined(reportNetSalesLastYear) && reportNetSalesLastYear.length && this._mapDataToNewValue(reportNetSales, reportNetSalesLastYear, "Net Sales");
        });
  }

  private _getUpdatedAnalyticsData(): void {
    combineLatest(
        this._store.select(fromStore.getEcommerceAnalyticsLoaded(this.website?.id))
    )
        .pipe(
            first(([analyticsLoaded]) => !!analyticsLoaded ),
            switchMap(() =>  combineLatest(
                this._store.select(fromStore.getEcommerceAnalyticsTotal(this.website.id, false)),
                this._store.select(fromStore.getEcommerceAnalyticsTotal(this.website.id, true)),
                this._store.select(fromStore.getEcommerceAnalyticsReportsOrder(this.website.id, false)),
                this._store.select(fromStore.getEcommerceAnalyticsReportsOrder(this.website.id, true)),
                this._store.select(fromStore.getEcommerceAnalyticsNetSales(this.website.id, false)),
                this._store.select(fromStore.getEcommerceAnalyticsNetSales(this.website.id, true))
            )),
        )
        .subscribe(([performanceLastMonth, performanceNow, reportOrders, reportOrdersLastYear, reportNetSales, reportNetSalesLastYear]): void => {
          this.performanceInfoLastYear = performanceLastMonth;
          this.performanceInfoNow = performanceNow;
          this.ordersData = TypeUtils.isDefined(reportOrders) && reportOrders.length && TypeUtils.isDefined(reportOrdersLastYear) && reportOrdersLastYear.length && this._mapDataToNewValue(reportOrders, reportOrdersLastYear, "Orders");
          this.netSalesData = TypeUtils.isDefined(reportNetSales) && reportNetSales.length && TypeUtils.isDefined(reportNetSalesLastYear) && reportNetSalesLastYear.length && this._mapDataToNewValue(reportNetSales, reportNetSalesLastYear, "Net Sales");
        });
  }

  ngOnDestroy() {

  }

}
